import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import OniiSyncButton from "../components/OniiSyncButton";
import { 
  Package, 
  ShoppingCart, 
  DollarSign, 
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Percent,
  Calendar,
  ArrowUp,
  ArrowDown,
  Minus
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KPICard = ({ title, value, subtitle, icon: Icon, color = "primary", alert, change, changeLabel }) => {
  const getChangeIcon = () => {
    if (change === undefined || change === null) return null;
    if (change > 0) return <ArrowUp className="w-3 h-3" />;
    if (change < 0) return <ArrowDown className="w-3 h-3" />;
    return <Minus className="w-3 h-3" />;
  };

  const getChangeColor = () => {
    if (change === undefined || change === null) return "text-muted-foreground";
    if (change > 0) return "text-green-600";
    if (change < 0) return "text-red-600";
    return "text-muted-foreground";
  };

  return (
    <Card className="kpi-card" data-testid={`kpi-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-0">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm text-muted-foreground mb-1">{title}</p>
            <p className={`kpi-value text-${color}`}>{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
            {change !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-xs ${getChangeColor()}`}>
                {getChangeIcon()}
                <span>{change > 0 ? '+' : ''}{change}%</span>
                {changeLabel && <span className="text-muted-foreground ml-1">{changeLabel}</span>}
              </div>
            )}
          </div>
          <div className={`w-10 h-10 rounded-sm flex items-center justify-center bg-${color}/10`}>
            <Icon className={`w-5 h-5 text-${color}`} strokeWidth={1.5} />
          </div>
        </div>
        {alert && (
          <div className="mt-3 pt-3 border-t border-border">
            <Badge variant="destructive" className="text-xs">{alert}</Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ComparisonRow = ({ label, currentValue, previousValue, change }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const getChangeColor = () => {
    if (change > 0) return "text-green-600 bg-green-50";
    if (change < 0) return "text-red-600 bg-red-50";
    return "text-muted-foreground bg-muted";
  };

  return (
    <div className="flex items-center justify-between py-2 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-3">
        <span className="text-sm font-mono">{formatCurrency(previousValue)}</span>
        <span className={`text-xs px-2 py-0.5 rounded ${getChangeColor()}`}>
          {change > 0 ? '+' : ''}{change}%
        </span>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [kpis, setKpis] = useState(null);
  const [stockAlerts, setStockAlerts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [kpisRes, alertsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/dashboard/kpis`),
        axios.get(`${API}/dashboard/stock-alerts`),
        axios.get(`${API}/sales/analytics`)
      ]);
      setKpis(kpisRes.data);
      setStockAlerts(alertsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Carregando...</div>
      </div>
    );
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const comparisons = kpis?.comparisons || {};

  return (
    <div className="p-8 animate-fadeIn" data-testid="dashboard-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">Visão geral do iFresh Market</p>
        </div>
        <OniiSyncButton onImportComplete={fetchData} />
      </div>

      {/* Main KPI Grid - Receita Bruta, Descontos, Receita Líquida, Ticket Médio */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <KPICard
          title="Receita Bruta"
          value={formatCurrency(kpis?.total_gross_revenue)}
          subtitle="total em vendas"
          icon={DollarSign}
          color="primary"
        />
        <KPICard
          title="Descontos"
          value={formatCurrency(kpis?.total_discounts)}
          subtitle="cashback e descontos"
          icon={Percent}
          color="destructive"
        />
        <KPICard
          title="Receita Líquida"
          value={formatCurrency(kpis?.total_revenue)}
          subtitle={`${kpis?.unique_transactions || 0} transações`}
          icon={DollarSign}
          color="primary"
        />
        <KPICard
          title="Ticket Médio"
          value={formatCurrency(kpis?.avg_ticket)}
          subtitle={`${kpis?.total_sales_items || 0} itens vendidos`}
          icon={ShoppingCart}
          color="primary"
        />
      </div>

      {/* Today's Performance + Comparisons */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Last Sale Day Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5" strokeWidth={1.5} />
              {kpis?.today?.label || "Último dia"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Receita</p>
                <p className="text-2xl font-bold font-mono">{formatCurrency(kpis?.today?.revenue)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Transações</p>
                <p className="text-2xl font-bold font-mono">{kpis?.today?.transactions || 0}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Ticket</p>
                <p className="text-2xl font-bold font-mono">{formatCurrency(kpis?.today?.ticket)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Comparisons Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5" strokeWidth={1.5} />
              Comparações (Receita)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ComparisonRow 
              label={comparisons.yesterday?.label || "vs Dia anterior"}
              previousValue={comparisons.yesterday?.revenue}
              change={comparisons.yesterday?.revenue_change}
            />
            <ComparisonRow 
              label="vs Mesmo dia semana passada"
              previousValue={comparisons.last_week_same_day?.revenue}
              change={comparisons.last_week_same_day?.revenue_change}
            />
            <ComparisonRow 
              label="vs Média 4 semanas (mesmo dia)"
              previousValue={comparisons.avg_4_same_weekdays?.revenue}
              change={comparisons.avg_4_same_weekdays?.revenue_change}
            />
          </CardContent>
        </Card>
      </div>

      {/* Secondary KPIs - Products and Alerts */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <KPICard
          title="Total Produtos"
          value={kpis?.total_products || 0}
          subtitle="cadastrados"
          icon={Package}
          color="primary"
        />
        <KPICard
          title="Estoque Zerado"
          value={kpis?.zero_stock || 0}
          subtitle="produtos sem estoque"
          icon={AlertTriangle}
          color="destructive"
          alert={kpis?.zero_stock > 0 ? `Atenção!` : null}
        />
        <KPICard
          title="Estoque Baixo"
          value={kpis?.low_stock || 0}
          subtitle="≤5 unidades"
          icon={Package}
          color="secondary"
        />
        <KPICard
          title="Transações"
          value={kpis?.unique_transactions || 0}
          subtitle="total histórico"
          icon={ShoppingCart}
          color="primary"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Top Products Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5" strokeWidth={1.5} />
              Top 10 Produtos Vendidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {analytics?.top_products?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.top_products.slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    width={150}
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) => value.length > 20 ? value.slice(0, 20) + '...' : value}
                  />
                  <Tooltip 
                    contentStyle={{ fontFamily: 'JetBrains Mono', fontSize: 12 }}
                    formatter={(value) => [value, 'Qtd']}
                  />
                  <Bar dataKey="total_qty" fill="hsl(143 49% 20%)" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Nenhum dado de vendas disponível
              </div>
            )}
          </CardContent>
        </Card>

        {/* Daily Revenue Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5" strokeWidth={1.5} />
              Receita por Dia
            </CardTitle>
          </CardHeader>
          <CardContent>
            {kpis?.daily_data?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={[...kpis.daily_data].reverse().slice(-30)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value) => value.slice(5)}
                  />
                  <YAxis 
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) => `R$${value.toFixed(0)}`}
                  />
                  <Tooltip 
                    contentStyle={{ fontFamily: 'JetBrains Mono', fontSize: 12 }}
                    formatter={(value) => [formatCurrency(value), 'Receita']}
                    labelFormatter={(label) => `Data: ${label}`}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="hsl(92 45% 32%)" 
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Nenhum dado de vendas disponível
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stock Alerts Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" strokeWidth={1.5} />
            Alertas de Estoque
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stockAlerts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="stock-alerts-table">
                <thead>
                  <tr>
                    <th>EAN</th>
                    <th>Produto</th>
                    <th>Estoque</th>
                    <th>Preço Custo</th>
                    <th>Preço Venda</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {stockAlerts.map((product) => (
                    <tr key={product.id}>
                      <td className="font-mono text-xs">{product.ean}</td>
                      <td className="max-w-[200px] truncate">{product.name}</td>
                      <td className="font-mono font-bold text-destructive">{product.quantity}</td>
                      <td className="font-mono">{formatCurrency(product.cost_price)}</td>
                      <td className="font-mono">{formatCurrency(product.sale_price)}</td>
                      <td>
                        <Badge 
                          variant={product.quantity === 0 ? "destructive" : "secondary"}
                          className="text-xs"
                        >
                          {product.quantity === 0 ? "ZERADO" : "BAIXO"}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum alerta de estoque</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
